import React, { useCallback, useEffect, useRef, useState } from "react";
import { UPLOAD_DONE_EVENT } from "@/context/UploadContext";
import { CsvPreview } from "@/components/inbox/CsvPreview";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Banknote, Eye, Settings2, RotateCcw, ListChecks, ChevronDown, ChevronUp, RefreshCw, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/formatDate";

const MATCH_STYLE = {
  matched: { label: "Rechnung zugeordnet", cls: "text-emerald-700" },
  amount_mismatch: { label: "Betrag abweichend", cls: "text-amber-700 font-medium" },
  not_found: { label: "Rechnung nicht gefunden", cls: "text-rose-700 font-medium" },
  probable: { label: "wahrscheinlich (Betrag + Datum)", cls: "text-amber-700" },
};

function PayoutTransactions({ p, companyId, onReconciled }) {
  const [busy, setBusy] = useState(false);
  const notFound = p.transactions.filter((t) => t.match_status === "not_found" && t.invoice).length;
  const resync = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/payout-reports/${p.id}/resync-request`);
      toast.success(`${r.data.requested} Rechnung(en) bei StrongFit nachgefordert (Webhook invoice.resync_requested)`);
      onReconciled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nachforderung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  const confirm = async (i) => {
    try {
      const r = await apiClient.post(`/companies/${companyId}/payout-reports/${p.id}/transactions/${i}/confirm`, {});
      toast.success(`Zuordnung bestätigt: ${r.data.transaction.invoice_number}`);
      onReconciled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bestätigung fehlgeschlagen");
    }
  };
  const rerun = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/payout-reports/${p.id}/reconcile`);
      toast.success(`Abgleich: ${r.data.matched} zugeordnet, ${r.data.unmatched} nicht gefunden, ${r.data.amount_mismatch} abweichend`);
      onReconciled();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ml-6 mb-2 rounded border border-slate-200 bg-slate-50/60 p-2" data-testid={`payout-transactions-${p.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-slate-500">
          {p.transactions.length} Transaktion{p.transactions.length !== 1 ? "en" : ""} aus dem Export
          {p.reconcile && ` · ${p.reconcile.matched} zugeordnet · ${p.reconcile.unmatched} nicht gefunden · ${p.reconcile.amount_mismatch} abweichend`}
        </span>
        <span className="inline-flex items-center gap-3">
          {notFound > 0 && (
            <button onClick={resync} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700 hover:bg-rose-100" data-testid={`payout-resync-${p.id}`}>
              <Send className="h-3 w-3" /> {p.resync_open ? `Bei StrongFit nachgefordert (${p.resync_open} offen) · erneut` : `Bei StrongFit nachfordern (${notFound})`}
            </button>
          )}
          <button onClick={rerun} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-violet-700 hover:underline" data-testid={`payout-reconcile-${p.id}`}>
            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Neu abgleichen
          </button>
        </span>
      </div>
      <table className="w-full text-[11px]">
        <thead className="text-slate-500 text-left">
          <tr>
            <th className="py-0.5 pr-2 font-medium">Bezahlt</th>
            <th className="py-0.5 pr-2 font-medium">Rechnung</th>
            <th className="py-0.5 pr-2 font-medium">Kunde</th>
            <th className="py-0.5 pr-2 font-medium">Karte</th>
            <th className="py-0.5 pr-2 font-medium text-right">Brutto</th>
            <th className="py-0.5 pr-2 font-medium text-right">Gebühr</th>
            <th className="py-0.5 pr-2 font-medium text-right">Netto</th>
            <th className="py-0.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {p.transactions.map((t, i) => {
            const ms = MATCH_STYLE[t.match_status] || { label: "—", cls: "text-slate-400" };
            return (
              <tr key={t.id || i} className="border-t border-slate-200" data-testid={`payout-tx-${p.id}-${i}`}>
                <td className="py-0.5 pr-2 whitespace-nowrap">{formatDate(t.date)} {t.time ? t.time.slice(0, 5) : ""}</td>
                <td className="py-0.5 pr-2 font-medium">
                  {t.invoice || t.invoice_number || "—"}
                  {t.match_status === "amount_mismatch" && t.invoice_total != null ? ` (Rechnung ${Number(t.invoice_total).toFixed(2)})` : ""}
                  {t.match_status === "probable" && t.candidate_count > 1 ? ` (+${t.candidate_count - 1} weitere Kandidaten)` : ""}
                  {!t.invoice && t.id ? <span className="ml-1 text-slate-400 font-normal">#{t.id}</span> : null}
                </td>
                <td className="py-0.5 pr-2 truncate max-w-[180px]" title={t.customer || ""}>{t.customer || "—"}</td>
                <td className="py-0.5 pr-2">{t.method || "—"}</td>
                <td className="py-0.5 pr-2 text-right tabular-nums">{Number(t.gross || 0).toFixed(2)}</td>
                <td className="py-0.5 pr-2 text-right tabular-nums">{Number(t.fee || 0).toFixed(2)}</td>
                <td className="py-0.5 pr-2 text-right tabular-nums">{Number(t.net || 0).toFixed(2)}</td>
                <td className={`py-0.5 ${ms.cls}`}>
                  {ms.label}
                  {t.confirmed_by && <span className="text-slate-400 font-normal"> · bestätigt</span>}
                  {t.match_status === "probable" && (
                    <button onClick={() => confirm(i)} className="ml-2 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800 hover:bg-emerald-100" data-testid={`payout-tx-confirm-${p.id}-${i}`}>
                      Bestätigen
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const fmtChf = (v) => (v == null ? "—"
  : new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(Number(v)));

const PROVIDER_STYLE = {
  sumup: "bg-amber-100 text-amber-800",
  payrexx: "bg-sky-100 text-sky-800",
  twint: "bg-slate-900 text-white",
};
const PROVIDER_LABEL = { sumup: "SumUp", payrexx: "Payrexx", twint: "TWINT" };
const PROVIDERS = ["sumup", "payrexx", "twint"];
const providerLabel = (p) => PROVIDER_LABEL[p] || "Payrexx";
const NEW_WINDOW_MS = 6 * 60 * 60 * 1000;

export function PayoutReportsCard({ companyId, reloadToken = 0 }) {
  const [items, setItems] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    sumup: "3400", payrexx: "3400", twint: "3400", sumup_auto: true, payrexx_auto: true, twint_auto: true,
  });
  const [revenueAccounts, setRevenueAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bookAcc, setBookAcc] = useState({});
  const [bookingId, setBookingId] = useState(null);
  const [openTx, setOpenTx] = useState({});

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/payout-reports`);
      const data = r.data || [];
      setItems(data);
      if (data.some((p) => p.needs_confirm)) {
        const a = await apiClient.get(`/companies/${companyId}/accounts`);
        setRevenueAccounts((a.data || []).filter((x) => (x.code || "").startsWith("3")));
      }
    } catch (e) {
      console.error(e);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const [selected, setSelected] = useState(new Set());
  const canDelete = (p) => !p.journal_entry_id && !p.split_booked;
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const removeSelected = async () => {
    const ids = [...selected];
    if (!ids.length || !window.confirm(`${ids.length} Auszahlungsbericht${ids.length === 1 ? "" : "e"} löschen? Dateien und Posteingangs-Einträge werden entfernt.`)) return;
    try {
      const r = await apiClient.post(`/companies/${companyId}/payout-reports/bulk-delete`, { ids });
      setItems((prev) => prev.filter((x) => !r.data.deleted.includes(x.id)));
      setSelected(new Set());
      toast.success(`${r.data.count} Bericht${r.data.count === 1 ? "" : "e"} gelöscht${r.data.skipped.length ? ` · ${r.data.skipped.length} übersprungen (bereits zugeordnet)` : ""}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  const removeReport = async (p) => {
    if (!window.confirm(`Auszahlungsbericht ${providerLabel(p.provider)} ${p.payout_date || ""} ${p.reference || ""} löschen? Datei und Posteingangs-Eintrag werden entfernt.`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/payout-reports/${p.id}`);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Auszahlungsbericht gelöscht.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bericht konnte nicht gelöscht werden.");
    }
  };


  // Nach einem Upload, der als Auszahlungsbericht erkannt wurde: neu laden, oben anpinnen und zur Karte scrollen
  const [highlight, setHighlight] = useState(new Set());
  const [showDone, setShowDone] = useState(false);
  const [csvView, setCsvView] = useState(null);
  const cardRef = useRef(null);
  useEffect(() => {
    const onDone = (e) => {
      const ids = e.detail?.payoutIds || [];
      if (e.detail?.companyId !== companyId || !ids.length) return;
      setHighlight((h) => new Set([...h, ...ids]));
      load().then(() => setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150));
    };
    window.addEventListener(UPLOAD_DONE_EVENT, onDone);
    return () => window.removeEventListener(UPLOAD_DONE_EVENT, onDone);
  }, [companyId, load]);

  const openSettings = async () => {
    try {
      const [s, a] = await Promise.all([
        apiClient.get(`/companies/${companyId}/payout-settings`),
        apiClient.get(`/companies/${companyId}/accounts`),
      ]);
      setSettings(Object.fromEntries(PROVIDERS.flatMap((prov) => [[prov, s.data[prov] || "3400"], [`${prov}_auto`, s.data[`${prov}_auto`] !== false]])));
      setRevenueAccounts((a.data || []).filter((x) => (x.code || "").startsWith("3")));
      setSettingsOpen(true);
    } catch (e) {
      toast.error("Einstellungen konnten nicht geladen werden");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/companies/${companyId}/payout-settings`, settings);
      toast.success("Einstellungen gespeichert — gilt für alle künftigen Auszahlungs-Buchungen");
      setSettingsOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const book = async (p) => {
    const acc = bookAcc[p.id] || p.suggested_revenue_account || "3400";
    setBookingId(p.id);
    try {
      await apiClient.post(`/companies/${companyId}/payout-reports/${p.id}/book`, {
        revenue_account: acc,
      });
      toast.success(`Auszahlung verbucht — Umsatz auf Konto ${acc}, Gebühren auf 6940`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verbuchen fehlgeschlagen");
    } finally {
      setBookingId(null);
    }
  };

  const unbook = async (p) => {
    try {
      await apiClient.post(`/companies/${companyId}/payout-reports/${p.id}/unbook`);
      toast.success("Buchung zurückgezogen — kann jetzt neu verbucht werden");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurückziehen fehlgeschlagen");
    }
  };

  if (items.length === 0) return null;

  const isNew = (p) => highlight.has(p.id) || (p.created_at && Date.now() - new Date(p.created_at).getTime() < NEW_WINDOW_MS);
  const isDone = (p) => !!p.journal_entry_id && !p.needs_confirm && !(p.provider !== "twint" && p.reconcile && (p.reconcile.unmatched || p.reconcile.amount_mismatch));
  const openItems = items.filter((p) => !isDone(p) || highlight.has(p.id));
  const doneItems = items.filter((p) => isDone(p) && !highlight.has(p.id));
  const visible = showDone ? [...openItems, ...doneItems] : openItems;
  const sortedItems = [...visible.filter(isNew), ...visible.filter((p) => !isNew(p))];
  const deletable = sortedItems.filter(canDelete);
  const allSelected = deletable.length > 0 && deletable.every((p) => selected.has(p.id));

  const openPdf = async (p) => {
    if (!p.has_pdf && p.has_export) {
      try {
        const r = await apiClient.get(`/companies/${companyId}/payout-reports/${p.id}/file`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        setCsvView({ url, name: p.file_name || `${providerLabel(p.provider)}-Export`, p });
      } catch (e) {
        toast.error("Export konnte nicht geladen werden");
      }
      return;
    }
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/payout-reports/${p.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("PDF konnte nicht geladen werden");
    }
  };

  const renderStatus = (p) => {
    if (p.split_booked) {
      return (
        <span className="flex items-center gap-1">
          <Badge
            className="bg-emerald-100 text-emerald-800"
            title="Die Auszahlung wurde als Umsatz + Gebühren verbucht"
            data-testid={`payout-status-booked-${p.id}`}
          >
            🟢 Verbucht{p.split_account ? ` (${p.split_account})` : ""}
          </Badge>
          {p.can_unbook && (
            <button
              onClick={() => unbook(p)}
              className="text-slate-400 hover:text-rose-600 transition-colors"
              title="Buchung zurückziehen — die ursprüngliche Netto-Buchung wird wiederhergestellt und du kannst neu verbuchen"
              data-testid={`payout-unbook-${p.id}`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </span>
      );
    }
    if (p.needs_confirm) {
      const selAcc = bookAcc[p.id] || p.suggested_revenue_account || "3400";
      return (
        <span className="flex items-center gap-1.5">
          <select
            value={selAcc}
            onChange={(e) => setBookAcc((m) => ({ ...m, [p.id]: e.target.value }))}
            className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]"
            title="Ertragskonto für den Umsatz — vorausgefüllt, kann geändert werden"
            data-testid={`payout-book-account-${p.id}`}
          >
            {!revenueAccounts.some((a) => a.code === selAcc) && (
              <option value={selAcc}>{selAcc}</option>
            )}
            {revenueAccounts.map((a) => (
              <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
            ))}
          </select>
          <Button
            size="sm"
            className="h-6 px-2 text-[11px] bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => book(p)}
            disabled={bookingId === p.id}
            title="Bestätigt den Umsatz-Split mit einem Klick"
            data-testid={`payout-book-btn-${p.id}`}
          >
            {bookingId === p.id ? "Verbuche…" : "Verbuchen"}
          </Button>
        </span>
      );
    }
    if (p.journal_entry_id) {
      return (
        <Badge
          className="bg-emerald-100 text-emerald-800"
          title={p.per_transaction ? `${p.matched_tx_count} Einzelgutschriften (TWINT überweist jede Zahlung separat) zugeordnet` : "Der Bericht ist der Bank-Gutschrift zugeordnet"}
          data-testid={`payout-status-matched-${p.id}`}
        >
          🟢 {p.per_transaction ? `${p.matched_tx_count} Gutschriften zugeordnet` : "Bank zugeordnet"}
        </Badge>
      );
    }
    if (p.per_transaction && p.matched_tx_count) {
      return (
        <Badge className="bg-sky-100 text-sky-800" title="TWINT überweist jede Zahlung separat – ein Teil der Gutschriften ist noch nicht im Bankauszug"
          data-testid={`payout-status-partial-${p.id}`}>
          🔵 {p.matched_tx_count}/{p.tx_count} Gutschriften
        </Badge>
      );
    }
    return (
      <Badge
        className="bg-amber-100 text-amber-800"
        title="Die Bank-Gutschrift ist noch nicht importiert — wird beim nächsten camt-Import automatisch zugeordnet"
        data-testid={`payout-status-waiting-${p.id}`}
      >
        🟡 Wartet auf Bankgutschrift
      </Badge>
    );
  };

  return (
    <Card ref={cardRef} className="p-4 border border-slate-200 bg-white scroll-mt-24" data-testid="payout-reports-card">
      <div className="flex items-center gap-2 mb-2">
        <Banknote className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-800">Auszahlungsberichte</h3>
        <span className="text-xs text-slate-400 flex-1">SumUp, Payrexx & TWINT — automatisch dem Bankabgleich zugeordnet</span>
        {deletable.length > 0 && (
          <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer" title="Alle wartenden Berichte auswählen">
            <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(deletable.map((p) => p.id)))} className="h-3.5 w-3.5 accent-slate-900" data-testid="payout-select-all" />
            Alle wartenden
          </label>
        )}
        {selected.size > 0 && (
          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={removeSelected} data-testid="payout-delete-selected">
            <Trash2 className="h-3 w-3 mr-1" />{selected.size} löschen
          </Button>
        )}
        {doneItems.length > 0 && (
          <button onClick={() => setShowDone((v) => !v)} className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline" data-testid="payout-toggle-done">
            {showDone ? "Erledigte ausblenden" : `${doneItems.length} erledigte anzeigen`}
          </button>
        )}
        <button
          onClick={openSettings}
          className="text-slate-400 hover:text-slate-800 transition-colors"
          title="Ertragskonto & Auto-Verbuchung pro Provider festlegen"
          data-testid="payout-settings-button"
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </div>
      {sortedItems.length === 0 && (
        <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2" data-testid="payout-all-done">Alle Auszahlungsberichte sind verbucht und der Bank zugeordnet.</p>
      )}
      <div className="divide-y divide-slate-100">
        {sortedItems.map((p) => (
          <React.Fragment key={p.id}>
          <div className={`flex items-center gap-2 py-1.5 text-xs ${isNew(p) ? "bg-amber-50/70 -mx-2 px-2 rounded" : ""} ${selected.has(p.id) ? "bg-rose-50/60" : ""}`} data-testid={`payout-report-${p.id}`}>
            {deletable.length > 0 && (
              canDelete(p)
                ? <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} className="h-3.5 w-3.5 accent-slate-900" title="Zum Löschen auswählen" data-testid={`payout-select-${p.id}`} />
                : <span className="w-3.5" />
            )}
            <Badge className={PROVIDER_STYLE[p.provider] || "bg-slate-100 text-slate-700"}>
              {providerLabel(p.provider)}
            </Badge>
            {isNew(p) && <Badge className="bg-amber-500 text-white" title="Vor kurzem hochgeladen" data-testid={`payout-new-${p.id}`}>Neu</Badge>}
            <span className="text-slate-600 w-20">{p.payout_date || "—"}</span>
            <span className="font-medium text-slate-800 w-28">{fmtChf(p.net)}</span>
            <span className="text-slate-400 truncate flex-1" title={p.reference || ""}>{p.reference || ""}</span>
            {p.transactions?.length > 0 && (
              <button
                onClick={() => setOpenTx((o) => ({ ...o, [p.id]: !o[p.id] }))}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${p.provider !== "twint" && p.reconcile && (p.reconcile.unmatched || p.reconcile.amount_mismatch) ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}
                title={p.provider === "twint" ? "Zahlungen anzeigen (TWINT-Ladenzahlungen haben keine Rechnung)" : "Transaktionen und Rechnungs-Zuordnung anzeigen"}
                data-testid={`payout-tx-toggle-${p.id}`}
              >
                <ListChecks className="h-3 w-3" />
                {p.provider === "twint" || !p.reconcile ? `${p.transactions.length} Zahlungen${p.matched_groups > 1 ? ` · ${p.matched_groups} Gutschriften` : ""}` : `${p.reconcile.matched}/${p.transactions.length} Rechnungen${p.reconcile.probable ? ` · ${p.reconcile.probable} ?` : ""}`}
                {openTx[p.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
            {renderStatus(p)}
            {(p.has_pdf || p.has_export) && (
              <button
                onClick={() => openPdf(p)}
                className="text-slate-500 hover:text-slate-900 transition-colors"
                title={p.has_pdf ? "Bericht-PDF anzeigen" : "Original-Export (CSV) anzeigen"}
                data-testid={`payout-report-pdf-${p.id}`}
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
            {!p.journal_entry_id && !p.split_booked && (
              <button
                onClick={() => removeReport(p)}
                className="text-slate-400 hover:text-rose-700 transition-colors"
                title="Bericht löschen (noch nicht der Bank zugeordnet)"
                data-testid={`payout-report-delete-${p.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {openTx[p.id] && p.transactions?.length > 0 && (
            <PayoutTransactions p={p} companyId={companyId} onReconciled={load} />
          )}
          </React.Fragment>
        ))}
      </div>
      <Dialog open={!!csvView} onOpenChange={(v) => { if (!v && csvView) { URL.revokeObjectURL(csvView.url); setCsvView(null); } }}>
        <DialogContent className="max-w-5xl" data-testid="payout-csv-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" />{csvView?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              {csvView && `${providerLabel(csvView.p.provider)} · Brutto CHF ${Number(csvView.p.gross || 0).toFixed(2)} − Gebühren CHF ${Number(csvView.p.fees || 0).toFixed(2)} = überwiesen CHF ${Number(csvView.p.net || 0).toFixed(2)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[60vh] border border-slate-200 rounded overflow-hidden">
            {csvView && <CsvPreview blobUrl={csvView.url} fileName={csvView.name} />}
          </div>
          <DialogFooter>
            <a href={csvView?.url} download={csvView?.name}><Button variant="outline" size="sm" data-testid="payout-csv-download">Herunterladen</Button></a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md" data-testid="payout-settings-dialog">
          <DialogHeader>
            <DialogTitle>Ertragskonten für Auszahlungen</DialogTitle>
            <DialogDescription className="sr-only">Ertragskonto und Auto-Verbuchung pro Zahlungsdienst</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Auf dieses Konto wird der Brutto-Umsatz gebucht (Gebühren gehen immer auf 6940).
            Mit dem Haken «Immer fix so buchen» geschieht das vollautomatisch — ohne Haken wird die
            Buchung nur vorgeschlagen und du bestätigst mit einem Klick.
          </p>
          <div className="space-y-4">
            {PROVIDERS.map((prov) => (
              <div key={prov}>
                <label className="text-xs font-medium text-slate-700">
                  {providerLabel(prov)} — Ertragskonto
                </label>
                <select
                  value={settings[prov]}
                  onChange={(e) => setSettings((s) => ({ ...s, [prov]: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                  data-testid={`payout-account-select-${prov}`}
                >
                  {!revenueAccounts.some((a) => a.code === settings[prov]) && (
                    <option value={settings[prov]}>{settings[prov]}</option>
                  )}
                  {revenueAccounts.map((a) => (
                    <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
                <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings[`${prov}_auto`]}
                    onChange={(e) => setSettings((s) => ({ ...s, [`${prov}_auto`]: e.target.checked }))}
                    className="accent-slate-900"
                    data-testid={`payout-auto-book-${prov}`}
                  />
                  Immer fix so buchen (automatisch verbuchen)
                </label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={saveSettings} disabled={saving} data-testid="payout-settings-save">
              {saving ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
